import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { Button } from '../components/ui';
import { fetchProductDetails } from '../features/products/productsSlice';
import { addToCartAsync } from '../features/cart/cartSlice';
import { loadProductDescription } from '../utils/markdownLoader';
import { translateProduct } from '../utils/productTranslations';
import { getProductGalleryImages } from '../utils/productCovers';
import { formatDisplayPrice } from '../utils/currencyDisplay';
import Seo from '../components/Seo';
import { getProductMeta } from '../seo/pageMeta';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const GalleryImage = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    className="block aspect-[16/10] h-auto w-full object-cover object-center"
  />
);

const ProductScreen = () => {
  const { t, i18n } = useTranslation();
  const [qty, setQty] = useState(1);
  const [detailedDescription, setDetailedDescription] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const { product, loading, error } = useSelector((state) => state.products);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    autoplay: false,
    adaptiveHeight: false,
    className: 'product-slider',
    dotsClass: 'slick-dots custom-dots',
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchProductDetails(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    const loadDescription = async () => {
      if (product?.slug) {
        try {
          const description = await loadProductDescription(product.slug, i18n.language);
          setDetailedDescription(description);
        } catch (err) {
          console.error('Erreur lors du chargement de la description:', err);
        }
      }
    };

    loadDescription();
  }, [product?.slug, i18n.language]);

  const addToCartHandler = () => {
    dispatch(addToCartAsync({ id, qty }));
    navigate('/cart');
  };

  const productMatches =
    product && String(product._id) === String(id);
  const pageReady = (!loading && productMatches) || (!loading && Boolean(error));
  const productMeta = getProductMeta(
    productMatches ? translateProduct(product, i18n.language).name : '',
    productMatches ? translateProduct(product, i18n.language).description : '',
    i18n.language
  );

  if (loading) {
    return (
      <>
        <Seo
          title={productMeta.title}
          description={productMeta.description}
          path={`/product/${id}`}
          ready={false}
        />
        <Loader />
      </>
    );
  }
  if (error) {
    return (
      <>
        <Seo
          title={productMeta.title}
          description={productMeta.description}
          path={`/product/${id}`}
          ready={pageReady}
        />
        <Message variant="danger">{error}</Message>
      </>
    );
  }
  if (!product) {
    return (
      <>
        <Seo
          title={productMeta.title}
          description={productMeta.description}
          path={`/product/${id}`}
          ready={pageReady}
        />
        <Message>{t('common.error')}</Message>
      </>
    );
  }

  const translatedProduct = translateProduct(product, i18n.language);
  const galleryImages = getProductGalleryImages(product);

  return (
    <div className="min-h-screen bg-bg pt-28 pb-24 md:pt-32">
      <Seo
        title={productMeta.title}
        description={productMeta.description}
        path={`/product/${id}`}
        ready={pageReady}
      />
      <style>
        {`
          .product-slider { position: relative; line-height: 0; }
          .product-slider.slick-dotted.slick-slider { margin-bottom: 0; }
          .product-slider .slick-list,
          .product-slider .slick-track,
          .product-slider .slick-slide,
          .product-slider .slick-slide > div {
            line-height: 0;
          }
          .product-slider .slick-slide > div {
            overflow: hidden;
            background: #0F1115;
          }
          .product-slider .slick-slide img {
            display: block;
            width: 100%;
            height: auto;
            object-fit: cover;
          }
          .product-slider .slick-dots {
            bottom: 14px;
            line-height: normal;
          }
          .product-slider .slick-dots li button:before {
            color: #EDEEF0;
            opacity: 0.35;
            font-size: 8px;
          }
          .product-slider .slick-dots li.slick-active button:before {
            color: #EDEEF0;
            opacity: 1;
          }
          .product-slider .slick-prev,
          .product-slider .slick-next {
            z-index: 10;
            width: 40px;
            height: 40px;
            background: rgba(8, 9, 11, 0.75);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 10px;
            transition: background 0.2s ease;
          }
          .product-slider .slick-prev:hover,
          .product-slider .slick-next:hover {
            background: rgba(255, 255, 255, 0.1);
          }
          .product-slider .slick-prev { left: 12px; }
          .product-slider .slick-next { right: 12px; }
          .product-slider .slick-prev:before,
          .product-slider .slick-next:before {
            font-size: 16px;
            color: #fff;
            opacity: 1;
          }
          .product-detail-markdown h2 {
            margin-top: 2.25rem;
            margin-bottom: 0.75rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid rgba(255, 255, 255, 0.08);
            color: #EDEEF0;
            font-size: 1.2rem;
            font-weight: 600;
          }
          .product-detail-markdown h2:first-child { margin-top: 0; }
          .product-detail-markdown h3 {
            margin-top: 1.35rem;
            margin-bottom: 0.5rem;
            color: #EDEEF0;
            font-size: 1.05rem;
            font-weight: 600;
          }
          .product-detail-markdown p { margin-top: 0.65rem; margin-bottom: 0.65rem; }
          .product-detail-markdown ul { margin-top: 0.5rem; margin-bottom: 0.75rem; }
          .product-detail-markdown blockquote {
            margin: 1.5rem 0;
            padding: 1rem 1.25rem;
            border-left: 2px solid rgba(91, 159, 212, 0.7);
            border-radius: 0 10px 10px 0;
            background: rgba(255, 255, 255, 0.03);
            font-style: normal;
            color: #EDEEF0;
          }
          .product-detail-markdown blockquote p { margin: 0; }
        `}
      </style>

      <div className="mx-auto max-w-page px-6">
        <Link
          to="/indicators"
          className="mb-10 inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-body"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          {t('product.backToProducts')}
        </Link>

        <div className="grid grid-cols-12 items-stretch gap-8 lg:gap-12">
          <div className="col-span-12 lg:col-span-8">
            <div className="h-full overflow-hidden rounded-card border border-white/[.08] bg-surface">
              {galleryImages.length > 1 ? (
                <Slider {...sliderSettings}>
                  {galleryImages.map((image, index) => (
                    <div key={`${image}-${index}`}>
                      <GalleryImage
                        src={image}
                        alt={`${translatedProduct.name} ${index + 1}`}
                      />
                    </div>
                  ))}
                </Slider>
              ) : (
                <GalleryImage src={galleryImages[0]} alt={translatedProduct.name} />
              )}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4">
            <div className="flex h-full flex-col rounded-card border border-white/[.08] bg-surface p-6">
              <h1 className="text-2xl font-semibold tracking-tight text-body md:text-3xl">
                {translatedProduct.name}
              </h1>

              <div className="mt-6 space-y-4">
                <div className="flex justify-between border-b border-white/[.06] pb-4 text-sm">
                  <span className="text-muted">{t('product.price')}</span>
                  <span className="font-medium text-body">
                    {translatedProduct.price === 0
                      ? t('product.free')
                      : formatDisplayPrice(translatedProduct.price, i18n.language)}
                  </span>
                </div>

                <div className="flex justify-between border-b border-white/[.06] pb-4 text-sm">
                  <span className="text-muted">{i18n.language === 'fr' ? 'Statut' : 'Status'}</span>
                  <span>
                    {translatedProduct.countInStock > 0 ? (
                      <span className="font-medium text-success">
                        {i18n.language === 'fr' ? 'En stock' : 'In stock'}
                      </span>
                    ) : (
                      <span className="font-medium text-danger">{t('product.outOfStock')}</span>
                    )}
                  </span>
                </div>

                {translatedProduct.countInStock > 0 && (
                  <div className="flex items-center justify-between border-b border-white/[.06] pb-4 text-sm">
                    <span className="text-muted">{t('product.quantity')}</span>
                    <select
                      value={qty}
                      onChange={(e) => setQty(Number(e.target.value))}
                      className="rounded-btn border border-white/10 bg-bg px-3 py-2 text-body focus:border-white/25 focus:outline-none"
                      aria-label={t('product.selectQuantity')}
                    >
                      {[...Array(translatedProduct.countInStock).keys()].map((x) => (
                        <option key={x + 1} value={x + 1}>
                          {x + 1}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <Button
                onClick={addToCartHandler}
                disabled={translatedProduct.countInStock === 0}
                className="mt-auto w-full"
              >
                {t('product.addToCart')}
              </Button>
            </div>
          </div>
        </div>

        {detailedDescription && (
          <div className="mt-14 rounded-card border border-white/[.08] bg-surface p-6 md:p-10">
            <div
              className="product-detail-markdown prose prose-invert prose-lg max-w-none text-muted prose-headings:font-semibold prose-headings:text-body prose-a:text-primary prose-strong:text-body prose-p:leading-relaxed prose-li:marker:text-muted"
              dangerouslySetInnerHTML={{ __html: detailedDescription }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductScreen;
